import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { CanonicalSafetyScanReport } from '../../components/safetyScanReport/CanonicalSafetyScanReport';
import { presentCanonicalSafetyScanReport } from '../../domain/safetyScanReport/canonicalSafetyScanReportPresenter';
import { presentScanControlRelation } from '../../domain/safetyCases/scanControlTemporalRelation';
import { CAMERA_MEASUREMENT_GOLDENS } from '../../domain/cameraMeasurement/cameraMeasurementGoldens';
import type { CameraActivityMeasurementResponse } from '../../services/cameraActivityMeasurementApi';
import type { ScanMeasurementSource } from '../../domain/cameraMeasurement/scanMeasurementIdentity';

const PRE_WINDOW = { start: '2026-08-08T16:00:00.000Z', end: '2026-08-09T16:00:00.000Z' };
const CROSS_WINDOW = { start: '2026-08-10T10:00:00.000Z', end: '2026-08-10T18:00:00.000Z' };
const POST_WINDOW = { start: '2026-08-12T16:00:00.000Z', end: '2026-08-13T16:00:00.000Z' };
const CONTROL_T = '2026-08-10T14:00:00.000Z';

const SOURCE: ScanMeasurementSource = {
  siteId: 'yyz',
  deviceId: 'demo5',
  cameraId: 'cam_walkway_zone_demo',
  windowStart: PRE_WINDOW.start,
  windowEnd: PRE_WINDOW.end,
};

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <Box data-testid={`sr2-golden-${id}`} sx={{ bgcolor: '#f4f6f9', border: '1px solid #e3e8ef', borderRadius: 1, mb: 2, p: 1.5 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.08, mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}

function report(args: Parameters<typeof presentCanonicalSafetyScanReport>[0]) {
  return presentCanonicalSafetyScanReport(args);
}

const preRelation = presentScanControlRelation({
  windowStart: PRE_WINDOW.start,
  windowEnd: PRE_WINDOW.end,
  controlRevisionId: 'ccr-1',
  implementedAt: CONTROL_T,
});
const crossingRelation = presentScanControlRelation({
  windowStart: CROSS_WINDOW.start,
  windowEnd: CROSS_WINDOW.end,
  controlRevisionId: 'ccr-1',
  implementedAt: CONTROL_T,
});
const postRelation = presentScanControlRelation({
  windowStart: POST_WINDOW.start,
  windowEnd: POST_WINDOW.end,
  controlRevisionId: 'ccr-1',
  implementedAt: CONTROL_T,
});

function sourceFor(window: { start: string; end: string }, signed = false): ScanMeasurementSource {
  return { ...SOURCE, windowStart: window.start, windowEnd: window.end, signed };
}

function layerB(window: { start: string; end: string }, override: CameraActivityMeasurementResponse) {
  return {
    ...override,
    window: { start: window.start, end: window.end },
  };
}

const Sr2GoldensFixturePage: React.FC = () => {
  const goldenA = report({
    displayedReportId: 'scan-a-baseline',
    persistedReport: {
      window_start_utc: PRE_WINDOW.start,
      window_end_utc: PRE_WINDOW.end,
      operating_condition: 'Recurring pedestrian activity at the walkway.',
      recurrence_headline: 'Repeats in this Scan window',
      confirmed_reviewed_observations: 5,
      usable_hours: 16,
      rates_available: true,
      overall_rate: 0.31,
    },
    locationLabel: 'Walkway Zone',
    conditionRaw: 'near_miss',
    controlRelation: preRelation,
    verification: {
      report_status: 'draft',
      state: 'FROZEN',
      window: PRE_WINDOW,
      progress: { total: 5, confirmed: 5, cleared: 0, unresolved: 0, unreviewed: 0, complete: true },
    },
    reconciliation: {
      verification: { total: 5, confirmed: 5, cleared: 0, unresolved: 0, unreviewed: 0, complete: true },
      measurement: {
        numerator: { value: 5 },
        denominator: { usable_hours: 16, proven: true },
        rate: { value: 0.31, issuable: true, withheld_reasons: [] },
        gates: { coverage_sufficient: true, rate_issuable: true },
      },
    },
    caseId: 'case-1',
  });
  const goldenD = report({
    displayedReportId: 'scan-d-unresolved',
    persistedReport: {
      window_start_utc: PRE_WINDOW.start,
      window_end_utc: PRE_WINDOW.end,
      operating_condition: 'Walkway activity under review.',
      confirmed_reviewed_observations: 2,
    },
    locationLabel: 'Walkway Zone',
    controlRelation: preRelation,
    verification: {
      report_status: 'draft',
      state: 'FROZEN',
      window: PRE_WINDOW,
      progress: { total: 4, confirmed: 2, cleared: 0, unresolved: 2, unreviewed: 0, complete: true },
    },
    reconciliation: {
      verification: { total: 4, confirmed: 2, cleared: 0, unresolved: 2, unreviewed: 0, complete: true },
      measurement: {
        numerator: { value: 2 },
        rate: { value: null, issuable: false, withheld_reasons: ['unresolved_members'] },
        gates: { rate_issuable: false },
      },
    },
  });
  const goldenE = report({
    displayedReportId: 'scan-e-ready',
    persistedReport: {
      window_start_utc: PRE_WINDOW.start,
      window_end_utc: PRE_WINDOW.end,
      operating_condition: 'Verified recurring walkway activity.',
      confirmed_reviewed_observations: 5,
      usable_hours: 16,
      rates_available: true,
      overall_rate: 0.31,
    },
    locationLabel: 'Walkway Zone',
    verification: {
      report_status: 'draft',
      state: 'FROZEN',
      window: PRE_WINDOW,
      progress: { total: 5, confirmed: 5, cleared: 0, unresolved: 0, unreviewed: 0, complete: true },
    },
    signing: {
      reportId: 'scan-e-ready',
      eligible: true,
      showSignCta: true,
      state: 'ready_to_sign',
      reasonCode: null,
      reason: '',
      unresolvedCount: 0,
      unresolvedDisclosed: false,
      isCurrentScan: true,
      isLatestSigned: false,
    },
  });
  const goldenF = report({
    displayedReportId: 'scan-f-signed',
    persistedReport: {
      window_start_utc: PRE_WINDOW.start,
      window_end_utc: PRE_WINDOW.end,
      status: 'finalized',
      operating_condition: 'Signed walkway Scan result.',
      confirmed_reviewed_observations: 5,
    },
    locationLabel: 'Walkway Zone',
    verification: {
      report_status: 'finalized',
      state: 'FINALIZED_IMMUTABLE',
      window: PRE_WINDOW,
      progress: { total: 5, confirmed: 5, cleared: 0, unresolved: 0, unreviewed: 0, complete: true },
    },
    signing: {
      reportId: 'scan-f-signed',
      eligible: false,
      showSignCta: false,
      state: 'already_signed',
      reasonCode: null,
      reason: '',
      unresolvedCount: 0,
      unresolvedDisclosed: false,
      isCurrentScan: false,
      isLatestSigned: true,
    },
  });
  const goldenI = report({
    displayedReportId: 'scan-i-current',
    firstEligibleReportId: 'scan-i-first',
    persistedReport: {
      window_start_utc: POST_WINDOW.start,
      window_end_utc: POST_WINDOW.end,
      operating_condition: 'Current Scan finding, not the first Scan.',
      confirmed_reviewed_observations: 3,
    },
    locationLabel: 'Walkway Zone',
    controlRelation: postRelation,
    verification: {
      report_status: 'draft',
      state: 'FROZEN',
      window: POST_WINDOW,
      progress: { total: 3, confirmed: 3, cleared: 0, unresolved: 0, unreviewed: 0, complete: true },
    },
    signing: {
      reportId: 'scan-i-current',
      eligible: true,
      showSignCta: false,
      state: 'verification_required',
      reasonCode: null,
      reason: '',
      unresolvedCount: 0,
      unresolvedDisclosed: false,
      isCurrentScan: true,
      isLatestSigned: false,
    },
  });
  const goldenJ = report({
    displayedReportId: 'scan-j-withheld',
    persistedReport: {
      window_start_utc: PRE_WINDOW.start,
      window_end_utc: PRE_WINDOW.end,
    },
    locationLabel: 'Walkway Zone',
    verification: {
      report_status: 'draft',
      state: 'FROZEN',
      window: PRE_WINDOW,
      progress: { total: 2, confirmed: 0, cleared: 0, unresolved: 0, unreviewed: 2, complete: false },
    },
    reconciliation: {
      measurement: {
        rate: { value: null, issuable: false, withheld_reasons: ['coverage_not_proven'] },
        gates: { coverage_sufficient: false, rate_issuable: false },
      },
    },
  });

  return (
    <Box data-testid="sr2-goldens-fixture" sx={{ bgcolor: '#eef2f6', minHeight: '100vh', p: 2 }}>
      <Typography component="h1" sx={{ fontSize: 18, fontWeight: 800, m: 0 }}>Edgentik · SR2 Safety Scan report goldens</Typography>
      <Typography sx={{ fontSize: 11, color: '#4a5768', mb: 2 }}>
        One report_id. Result before workflow. Camera Measurement is context, not the claim.
      </Typography>
      <Stack spacing={0}>
        <Section id="A" title="A. PRE / Baseline · verified · recurrence · Layer B MEASURED">
          <CanonicalSafetyScanReport
            view={goldenA}
            measurementSource={sourceFor(PRE_WINDOW)}
            measurementOverride={layerB(PRE_WINDOW, CAMERA_MEASUREMENT_GOLDENS.measured)}
          />
        </Section>
        <Section id="B" title="B. Layer B MEASURED ZERO 0s/0% not missing">
          <CanonicalSafetyScanReport
            view={goldenA}
            measurementSource={sourceFor(PRE_WINDOW)}
            measurementOverride={layerB(PRE_WINDOW, CAMERA_MEASUREMENT_GOLDENS.measuredZero)}
            measurementDefaultExpanded
          />
        </Section>
        <Section id="C" title="C. Layer B LIMITED values withheld">
          <CanonicalSafetyScanReport
            view={goldenA}
            measurementSource={sourceFor(PRE_WINDOW)}
            measurementOverride={layerB(PRE_WINDOW, CAMERA_MEASUREMENT_GOLDENS.limitedProducerGap)}
            measurementDefaultExpanded
          />
        </Section>
        <Section id="D" title="D. Unresolved members · not fully claimable">
          <CanonicalSafetyScanReport view={goldenD} measurementSource={sourceFor(PRE_WINDOW)} measurementOverride={layerB(PRE_WINDOW, CAMERA_MEASUREMENT_GOLDENS.measured)} />
        </Section>
        <Section id="E" title="E. Fully verified · ready to sign · not signed">
          <CanonicalSafetyScanReport view={goldenE} measurementSource={sourceFor(PRE_WINDOW)} measurementOverride={layerB(PRE_WINDOW, CAMERA_MEASUREMENT_GOLDENS.measured)} />
        </Section>
        <Section id="F" title="F. Signed Scan · Signed Record link · Layer B not part of signed result">
          <CanonicalSafetyScanReport
            view={goldenF}
            measurementSource={sourceFor(PRE_WINDOW, true)}
            measurementOverride={layerB(PRE_WINDOW, CAMERA_MEASUREMENT_GOLDENS.measured)}
            measurementDefaultExpanded
          />
        </Section>
        <Section id="G" title="G. CROSSING · Crosses the recorded Control date">
          <CanonicalSafetyScanReport
            view={report({
              displayedReportId: 'scan-g-crossing',
              persistedReport: {
                window_start_utc: CROSS_WINDOW.start,
                window_end_utc: CROSS_WINDOW.end,
                operating_condition: 'Crossing-window Scan retained.',
                confirmed_reviewed_observations: 2,
              },
              locationLabel: 'Walkway Zone',
              controlRelation: crossingRelation,
              verification: {
                window: CROSS_WINDOW,
                progress: { total: 2, confirmed: 2, cleared: 0, unresolved: 0, unreviewed: 0, complete: true },
              },
            })}
            measurementSource={sourceFor(CROSS_WINDOW)}
            measurementOverride={layerB(CROSS_WINDOW, CAMERA_MEASUREMENT_GOLDENS.measured)}
          />
        </Section>
        <Section id="H" title="H. POST / Follow-up · no Evaluation verdict">
          <CanonicalSafetyScanReport
            view={report({
              displayedReportId: 'scan-h-followup',
              persistedReport: {
                window_start_utc: POST_WINDOW.start,
                window_end_utc: POST_WINDOW.end,
                operating_condition: 'Follow-up Scan result.',
                confirmed_reviewed_observations: 1,
              },
              locationLabel: 'Walkway Zone',
              controlRelation: postRelation,
              verification: {
                window: POST_WINDOW,
                progress: { total: 1, confirmed: 1, cleared: 0, unresolved: 0, unreviewed: 0, complete: true },
              },
            })}
            measurementSource={sourceFor(POST_WINDOW)}
            measurementOverride={layerB(POST_WINDOW, CAMERA_MEASUREMENT_GOLDENS.measured)}
          />
        </Section>
        <Section id="I" title="I. Multiple Scans · displayed report_id · Current vs Latest Signed">
          <CanonicalSafetyScanReport
            view={goldenI}
            measurementSource={sourceFor(POST_WINDOW)}
            measurementOverride={layerB(POST_WINDOW, CAMERA_MEASUREMENT_GOLDENS.measured)}
          />
        </Section>
        <Section id="J" title="J. No usable claim / withheld · no invented zeros">
          <CanonicalSafetyScanReport
            view={goldenJ}
            measurementSource={sourceFor(PRE_WINDOW)}
            measurementOverride={layerB(PRE_WINDOW, CAMERA_MEASUREMENT_GOLDENS.notMeasuredTelemetry)}
            measurementDefaultExpanded
          />
        </Section>
      </Stack>
    </Box>
  );
};

export default Sr2GoldensFixturePage;
